import React from 'react';
import { formatDate } from '../../../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { User, Phone, Mail, Droplet, AlertTriangle, UserCog, Edit } from 'lucide-react';

const Row = ({ label, children, align = false }) => (
  <div className={`flex justify-between ${align ? 'items-center' : ''}`}>
    <span className="text-sm text-slate-500">{label}</span>
    {children}
  </div>
);

const SectionCard = ({ icon: Icon, title, children }) => (
  <Card>
    <CardHeader>
      <CardTitle className="text-lg font-semibold font-[Manrope] flex items-center gap-2">
        {Icon && <Icon className="w-5 h-5 text-red-500" />}
        {title}
      </CardTitle>
    </CardHeader>
    <CardContent className="space-y-3">{children}</CardContent>
  </Card>
);

const Empty = ({ text }) => <p className="text-sm text-slate-400 text-center py-4">{text}</p>;

export const PatientOverviewTab = ({ patient, doctors, nurses, canEdit, onEdit }) => {
  const doctorName = doctors.find(d => d.id === patient.assigned_doctor_id)?.full_name || 'Unknown';
  const nurseName = nurses.find(n => n.id === patient.assigned_nurse_id)?.full_name || 'Unknown';

  return (
    <div className="space-y-6" data-testid="patient-overview-tab">
      {canEdit && (
        <div className="flex justify-end">
          <Button className="bg-blue-500 hover:bg-blue-600" onClick={onEdit} data-testid="edit-patient-btn">
            <Edit className="w-4 h-4 mr-2" />
            Edit Patient Details
          </Button>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <SectionCard icon={User} title="Personal Information">
          <Row label="Full Name:"><span className="text-sm font-medium">{patient.full_name}</span></Row>
          {patient.date_of_birth && <Row label="Date of Birth:"><span className="text-sm font-medium">{formatDate(patient.date_of_birth)}</span></Row>}
          {patient.gender && <Row label="Gender:"><span className="text-sm font-medium capitalize">{patient.gender}</span></Row>}
          {patient.phone && (
            <Row label="Phone:" align>
              <span className="text-sm font-medium flex items-center gap-2"><Phone className="w-3 h-3" />{patient.phone}</span>
            </Row>
          )}
          {patient.email && (
            <Row label="Email:" align>
              <span className="text-sm font-medium flex items-center gap-2"><Mail className="w-3 h-3" />{patient.email}</span>
            </Row>
          )}
          {patient.address && <Row label="Address:"><span className="text-sm font-medium text-right max-w-xs">{patient.address}</span></Row>}
          {patient.blood_group && (
            <Row label="Blood Group:" align>
              <Badge className="bg-red-100 text-red-800"><Droplet className="w-3 h-3 mr-1" />{patient.blood_group}</Badge>
            </Row>
          )}
        </SectionCard>

        <SectionCard icon={AlertTriangle} title="Emergency Contact">
          {patient.emergency_contact && <Row label="Name:"><span className="text-sm font-medium">{patient.emergency_contact}</span></Row>}
          {patient.emergency_phone && (
            <Row label="Phone:" align>
              <span className="text-sm font-medium flex items-center gap-2"><Phone className="w-3 h-3" />{patient.emergency_phone}</span>
            </Row>
          )}
          {!patient.emergency_contact && !patient.emergency_phone && <Empty text="No emergency contact information" />}
        </SectionCard>

        <SectionCard icon={UserCog} title="Assignment">
          {patient.specialty && <Row label="Specialty:"><Badge className="bg-purple-100 text-purple-800">{patient.specialty}</Badge></Row>}
          {patient.assigned_doctor_id && <Row label="Assigned Doctor:"><span className="text-sm font-medium">Dr. {doctorName}</span></Row>}
          {patient.assigned_nurse_id && patient.patient_type === 'indoor' && (
            <Row label="Assigned Nurse:"><span className="text-sm font-medium">{nurseName}</span></Row>
          )}
          {!patient.specialty && !patient.assigned_doctor_id && <Empty text="No assignment yet" />}
        </SectionCard>

        <SectionCard title="Medical Information">
          {patient.allergies && (
            <div>
              <span className="text-sm text-slate-500 block mb-1">Allergies:</span>
              <Badge className="bg-yellow-100 text-yellow-800"><AlertTriangle className="w-3 h-3 mr-1" />{patient.allergies}</Badge>
            </div>
          )}
          {patient.medical_history && (
            <div>
              <span className="text-sm text-slate-500 block mb-1">Medical History:</span>
              <p className="text-sm">{patient.medical_history}</p>
            </div>
          )}
          {!patient.allergies && !patient.medical_history && <Empty text="No medical information" />}
        </SectionCard>
      </div>
    </div>
  );
};
